/**
 * Lista e ficha de clientes (docs/design/pranchas/01-clientes.png): cada linha diz o que importa
 * daquela cliente agora (em atendimento, pedido, pacote, próximo horário, sumiu ou última visita),
 * e a ficha mostra visitas, faltas, quanto costuma gastar e a cartela do pacote.
 */
import type { Appointment, AppointmentService, Client, Package, Payment, PaymentMethod, Service } from '../schemas/entities';
import { includesService } from './appointment-services';
import { occupiesTime } from './appointments';
import { WEEKDAY_NAMES } from './defaults';
import { formatDayMonth, formatHourOf } from './format';
import type { MissingClient } from './missing';
import { packageBalance } from './packages';
import { type LocalDate, diffDays, toMs, todayIn, utcToZoned, weekdayOf } from './time';

export type ClientLine =
  | { kind: 'now'; service: string | null }
  | { kind: 'pending'; start: string }
  | { kind: 'package'; used: number; total: number; next: string | null }
  | {
      kind: 'next';
      start: string;
      service: string | null;
      unconfirmed: boolean;
    }
  | { kind: 'missing'; days: number }
  | { kind: 'last'; at: string; service: string | null }
  | { kind: 'new' };

export interface ClientRow {
  client: Client;
  line: ClientLine;
  /** pacote ativo (com saldo e dentro da validade) */
  activePackage: { used: number; total: number; remaining: number } | null;
  missing: boolean;
}

const waiting = (a: Appointment) => a.status === 'scheduled' || a.status === 'confirmed';

export function clientRows(params: {
  clients: Client[];
  appointments: Appointment[];
  services: Service[];
  packages: Package[];
  missing: MissingClient[];
  /** linhas de serviço; sem elas, casa o pacote pelo serviço do atendimento */
  appointmentServices?: AppointmentService[];
  timezone: string;
  now: number;
}): ClientRow[] {
  const { now, timezone } = params;
  const appointmentServices = params.appointmentServices ?? [];
  const today = todayIn(timezone, now);
  const serviceName = (id: string) => params.services.find((s) => s.id === id)?.name ?? null;
  const missingById = new Map(params.missing.map((m) => [m.client.id, m]));
  const byClient = new Map<string, Appointment[]>();
  for (const a of params.appointments) {
    if (a.deleted_at) continue;
    const list = byClient.get(a.client_id) ?? [];
    list.push(a);
    byClient.set(a.client_id, list);
  }

  return params.clients
    .filter((c) => !c.deleted_at)
    .map((client) => {
      const mine = (byClient.get(client.id) ?? []).sort((a, b) => toMs(a.start_utc) - toMs(b.start_utc));
      const pkg = params.packages
        .filter((p) => !p.deleted_at && p.client_id === client.id)
        .map((p) => ({ p, b: packageBalance(p, params.appointments, today) }))
        .find((x) => x.b.active);
      const activePackage = pkg ? { used: pkg.b.used, total: pkg.b.total, remaining: pkg.b.remaining } : null;
      const current = mine.find((a) => waiting(a) && toMs(a.start_utc) <= now && now < toMs(a.end_utc));
      const pending = mine.find((a) => a.status === 'pending' && toMs(a.start_utc) >= now);
      const next = mine.find((a) => waiting(a) && toMs(a.start_utc) > now);
      const missing = missingById.get(client.id);
      const last = [...mine].reverse().find((a) => a.status === 'completed');

      let line: ClientLine;
      if (current) line = { kind: 'now', service: serviceName(current.service_id) };
      else if (pending) line = { kind: 'pending', start: pending.start_utc };
      else if (pkg) {
        const nextOfPkg = mine.find((a) => waiting(a) && includesService(a, pkg.p.service_id, appointmentServices) && toMs(a.start_utc) > now);
        line = {
          kind: 'package',
          used: pkg.b.used,
          total: pkg.b.total,
          next: nextOfPkg?.start_utc ?? null,
        };
      } else if (next) {
        line = {
          kind: 'next',
          start: next.start_utc,
          service: serviceName(next.service_id),
          // quem marcou pelo link escolheu o horário sozinha; só pede confirmação quem foi marcada pela profissional
          unconfirmed: next.status === 'scheduled' && next.source !== 'link' && utcToZoned(next.start_utc, timezone).date === today,
        };
      } else if (missing) line = { kind: 'missing', days: missing.daysSince };
      else if (last)
        line = {
          kind: 'last',
          at: last.start_utc,
          service: serviceName(last.service_id),
        };
      else line = { kind: 'new' };

      return { client, line, activePackage, missing: Boolean(missing) };
    })
    .sort((a, b) => a.client.name.localeCompare(b.client.name, 'pt-BR'));
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "Hoje 10h30", "Amanhã 9h30", "Quinta 22/10, 10h" (com maiúscula) ou o mesmo em minúscula; `explicit` sempre com a data. */
export function dayAndHour(start: string, timezone: string, today: LocalDate, opts: { lower?: boolean; explicit?: boolean } = {}): string {
  const { date } = utcToZoned(start, timezone);
  const hour = formatHourOf(start, timezone);
  const d = opts.explicit ? NaN : diffDays(today, date);
  const day = d === 0 ? 'hoje' : d === 1 ? 'amanhã' : `${WEEKDAY_NAMES[weekdayOf(date)]} ${formatDayMonth(date)},`;
  const text = `${day} ${hour}`;
  return opts.lower ? text : capitalize(text);
}

/**
 * Texto da linha da lista. `lead` é a pílula coral "Agora"; `warn` vai em âmbar com o ícone de atenção.
 */
export function clientLineText(line: ClientLine, timezone: string, today: LocalDate): { lead?: 'Agora'; text: string; warn?: string } {
  switch (line.kind) {
    case 'now':
      return { lead: 'Agora', text: line.service ?? 'Em atendimento' };
    case 'pending':
      return {
        text: `Pedido: ${dayAndHour(line.start, timezone, today, { lower: true, explicit: true })}`,
      };
    case 'package':
      return {
        text: `Pacote: ${line.used} de ${line.total} feitas${line.next ? `, ${dayAndHour(line.next, timezone, today, { lower: true })}` : ''}`,
      };
    case 'next': {
      const when = dayAndHour(line.start, timezone, today);
      if (line.unconfirmed) return { text: when, warn: 'não confirmou' };
      return {
        text: line.service ? `${when}, ${line.service.toLowerCase()}` : when,
      };
    }
    case 'missing':
      return { text: '', warn: `Não vem há ${line.days} dias` };
    case 'last': {
      const { date } = utcToZoned(line.at, timezone);
      const day = `${WEEKDAY_NAMES[weekdayOf(date)]} ${formatDayMonth(date)}`;
      return {
        text: line.service ? `${line.service} na ${day}` : `Última vez na ${day}`,
      };
    }
    default:
      return { text: 'Ainda sem atendimento' };
  }
}

/** Visitas, faltas, quanto costuma gastar e a última visita. */
export function clientStats(
  appointments: Appointment[],
  payments: Payment[],
  timezone: string,
): {
  visits: number;
  noShows: number;
  usualCents: number | null;
  lastVisit: LocalDate | null;
} {
  const done = appointments.filter((a) => !a.deleted_at && a.status === 'completed');
  const paid = new Map<string, number>();
  for (const p of payments) if (!p.deleted_at && p.appointment_id) paid.set(p.appointment_id, (paid.get(p.appointment_id) ?? 0) + p.amount_cents);
  const amounts = done
    .filter((a) => !a.package_id)
    .map((a) => paid.get(a.id) ?? 0)
    .filter((v) => v > 0);
  const usual = amounts.length ? Math.round(amounts.reduce((s, v) => s + v, 0) / amounts.length / 100) * 100 : null;
  const last = done.sort((a, b) => toMs(b.start_utc) - toMs(a.start_utc))[0];
  return {
    visits: done.length,
    noShows: appointments.filter((a) => !a.deleted_at && a.status === 'no_show').length,
    usualCents: usual,
    lastVisit: last ? utcToZoned(last.start_utc, timezone).date : null,
  };
}

/** Desde quando é cliente: cadastro ou o primeiro atendimento, o que vier antes. */
export function clientSince(client: Client, appointments: Appointment[], timezone: string): LocalDate | null {
  const dates = appointments.filter((a) => !a.deleted_at && occupiesTime(a)).map((a) => toMs(a.start_utc));
  if (client.created_at) dates.push(toMs(client.created_at));
  if (dates.length === 0) return null;
  return utcToZoned(Math.min(...dates), timezone).date;
}

/** Forma de pagamento de cada atendimento (a do último pagamento). */
export function paymentMethodByAppointment(payments: Payment[]): Map<string, PaymentMethod> {
  const out = new Map<string, { m: PaymentMethod; at: number }>();
  for (const p of payments) {
    if (p.deleted_at || !p.appointment_id) continue;
    const at = toMs(p.paid_at);
    const prev = out.get(p.appointment_id);
    if (!prev || at > prev.at) out.set(p.appointment_id, { m: p.method, at });
  }
  return new Map([...out.entries()].map(([k, v]) => [k, v.m]));
}

export type PackageCell = { n: number; state: 'done'; date: LocalDate | null } | { n: number; state: 'next'; start: string } | { n: number; state: 'open' };

/**
 * Cartela do pacote: as feitas (com a data, quando foram no app), a próxima marcada e as livres.
 * As feitas antes do cadastro do pacote entram sem data.
 */
export function packageCells(
  pkg: Package,
  appointments: Appointment[],
  timezone: string,
  now: number,
  appointmentServices: AppointmentService[] = [],
): PackageCell[] {
  const usedHere = appointments
    .filter((a) => !a.deleted_at && a.package_id === pkg.id && a.status === 'completed')
    .sort((a, b) => toMs(a.start_utc) - toMs(b.start_utc));
  const next = appointments
    // includesService e não `a.service_id === ...`: o serviço do pacote pode
    // ser o segundo do atendimento.
    .filter((a) => !a.deleted_at && a.client_id === pkg.client_id && includesService(a, pkg.service_id, appointmentServices) && waiting(a) && toMs(a.end_utc) > now)
    .sort((a, b) => toMs(a.start_utc) - toMs(b.start_utc))[0];
  const cells: PackageCell[] = [];
  for (let i = 0; i < pkg.sessions_used_initial && cells.length < pkg.sessions_total; i++) cells.push({ n: cells.length + 1, state: 'done', date: null });
  for (const a of usedHere) {
    if (cells.length >= pkg.sessions_total) break;
    cells.push({
      n: cells.length + 1,
      state: 'done',
      date: utcToZoned(a.start_utc, timezone).date,
    });
  }
  if (next && cells.length < pkg.sessions_total) cells.push({ n: cells.length + 1, state: 'next', start: next.start_utc });
  while (cells.length < pkg.sessions_total) cells.push({ n: cells.length + 1, state: 'open' });
  return cells;
}

/** Número da sessão de cada atendimento que usou o pacote (as anteriores ao cadastro contam antes). */
export function packageSessionNumbers(pkg: Package, appointments: Appointment[]): Map<string, number> {
  const used = appointments
    .filter((a) => !a.deleted_at && a.package_id === pkg.id && a.status === 'completed')
    .sort((a, b) => toMs(a.start_utc) - toMs(b.start_utc));
  return new Map(used.map((a, i) => [a.id, pkg.sessions_used_initial + i + 1]));
}
